import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useInvoiceData, Invoice } from './hooks/useInvoiceData';
import DataDashboard, { Column } from '../../components/common/DataDashboard';
import documentApi from '../../services/documentApi';
import InvoiceSettingsModal from './components/InvoiceSettingsModal';
import ManualLinkModal from './components/ManualLinkModal';
import BulkMatchModal from './components/BulkMatchModal';
import DiscrepancyModal from '../BulkUpload/components/DiscrepancyModal';
import './InvoiceReconciliation.scss';

// Currency code normalization (TL -> TRY, etc.)
const CURRENCY_MAP: Record<string, string> = { 'TL': 'TRY', '₺': 'TRY', '$': 'USD', '€': 'EUR', '£': 'GBP', 'Fr': 'CHF' };
const normalizeCurrency = (c?: string) => c ? (CURRENCY_MAP[c] || CURRENCY_MAP[c.toUpperCase()] || c.toUpperCase()) : 'TRY';

// Utility to format currency
const formatCurrency = (amount?: number, currency = 'TRY') => {
    if (amount === undefined || amount === null) return '-';
    const code = normalizeCurrency(currency);
    try {
        return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: code }).format(amount);
    } catch {
        return `${amount.toLocaleString('tr-TR')} ${code}`;
    }
};

const InvoiceReconciliation: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const filterParam = searchParams.get('filter'); // 'matched', 'discrepancy', 'unmatched', or null

    const { data, isLoading, error, pagination, fetchData } = useInvoiceData(filterParam || undefined);
    const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
    const [linkInvoiceId, setLinkInvoiceId] = useState<string | null>(null);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [isBulkMatchOpen, setIsBulkMatchOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const FILTER_LABELS: Record<string, string> = {
        matched: 'Matched',
        discrepancy: 'Discrepancies',
        unmatched: 'Pending Review',
    };

    const clearFilter = useCallback(() => {
        setSearchParams({});
    }, [setSearchParams]);

    // Fetch with filter when filter changes
    useEffect(() => {
        fetchData({ matchStatus: filterParam || undefined });
    }, [filterParam]);

    // Wrapper to include filter in all fetch calls
    const handleDataFetch = useCallback((params: any) => {
        fetchData({ ...params, matchStatus: filterParam || undefined });
    }, [fetchData, filterParam]);

    // Flatten nested data for sorting/filtering
    const flattenedData = useMemo(() => {
        return data.map(inv => ({
            ...inv,
            // Flatten for column key matching - support both old and Document AI formats
            status: inv.comparison?.status || inv.status || 'pending',
            invoice_details: inv.extracted_data?.invoice_number || inv.file_name || '',
            vendor: inv.extracted_data?.vendor?.name || inv.extracted_data?.supplier_name || 'Unknown Vendor',
            amount: inv.extracted_data?.financials?.total_amount || inv.extracted_data?.total_amount || 0,
            linked_rfq: inv.comparison?.linked_quote_id ? 'Linked' : 'Unlinked',
            // Editable fields (keys match PATCH endpoint field names)
            vendor_name: inv.extracted_data?.vendor?.name || inv.extracted_data?.supplier_name || '',
            invoice_number: inv.extracted_data?.invoice_number || '',
            invoice_date: inv.extracted_data?.invoice_date || '',
            total_amount: inv.extracted_data?.financials?.total_amount || inv.extracted_data?.total_amount || 0,
            supplier_tax_id: inv.extracted_data?.supplier_tax_id || inv.extracted_data?.vendor?.tax_id || '',
        }));
    }, [data]);

    // Adapt Invoice to MatchResult for Modal
    const getMatchResult = (inv: Invoice): any => {
        return {
            id: inv.id,
            invoiceFile: inv.file_name,
            quoteFile: inv.comparison?.linked_quote_id ? `Quote (${inv.comparison.linked_quote_id.substring(0, 8)}...)` : 'Linked Quote',
            status: inv.comparison?.status || 'pending',
            confidence: Math.round((inv.comparison?.match_score || 0) * 100),
            details: '',
            verification: inv.comparison?.verification_result
        };
    };

    const handleLinkSuccess = () => {
        handleDataFetch(pagination); // Refresh current page with filter
    };

    const handleSaveChanges = useCallback(async (changes: Record<string, Record<string, any>>) => {
        try {
            const promises = Object.entries(changes).map(([docId, fields]) =>
                documentApi.updateDocumentFields(docId, fields)
            );
            await Promise.all(promises);
            setIsEditing(false);
            handleDataFetch(pagination);
        } catch (err) {
            console.error('Failed to save changes:', err);
        }
    }, [handleDataFetch, pagination]);

    // Columns Definition
    const columns: Column[] = [
        {
            key: 'status',
            title: 'Status',
            type: 'status',
            width: '120px',
            sortable: true,
            filterable: true,
            render: (value, row: Invoice) => {
                const status = row.comparison?.status || row.status || 'pending';
                let className = 'status-cell';
                if (status === 'matched') className += ' matched';
                else if (status === 'discrepancy') className += ' discrepancy';
                else className += ' pending_review';

                return (
                    <span className={`badge ${className}`}>
                        {status.replace(/_/g, ' ').toUpperCase()}
                    </span>
                );
            }
        },
        {
            key: 'invoice_details',
            title: 'Invoice Details',
            type: 'text',
            sortable: true,
            filterable: true,
            render: (_, row: Invoice) => (
                <div className="cell-stacked">
                    <span className="primary-value">{row.extracted_data?.invoice_number || row.file_name}</span>
                    <span className="secondary-value">{row.extracted_data?.invoice_date || (row.uploaded_at ? row.uploaded_at.split('T')[0] : '-')}</span>
                </div>
            )
        },
        {
            key: 'vendor',
            title: 'Vendor',
            type: 'text',
            sortable: true,
            filterable: true,
            render: (_, row: Invoice) => (
                <div className="cell-stacked">
                    <span className="primary-value">{row.extracted_data?.vendor?.name || row.extracted_data?.supplier_name || 'Unknown Vendor'}</span>
                    <span className="secondary-value">{row.extracted_data?.vendor?.tax_id || row.extracted_data?.supplier_tax_id || '-'}</span>
                </div>
            )
        },
        {
            key: 'amount',
            title: 'Reconciliation (Inv / Quote)',
            type: 'number',
            sortable: true,
            filterable: true,
            render: (_, row: Invoice) => {
                // Support both nested (financials.total_amount) and flat (total_amount) formats
                const invAmount = row.extracted_data?.financials?.total_amount || row.extracted_data?.total_amount;
                const currency = row.extracted_data?.financials?.currency || row.extracted_data?.currency || 'TRY';
                const quoteAmount = row.comparison?.expected_amount;
                const matchScore = row.comparison?.match_score;

                // Determine if there is a significant difference
                const hasDiff = row.comparison?.status === 'discrepancy';

                return (
                    <div className={`cell-stacked ${hasDiff ? 'has-diff' : ''} ${row.comparison?.status === 'matched' ? 'matched' : ''}`}>
                        <span className="primary-value">
                            {formatCurrency(invAmount, currency)}
                        </span>
                        <span className="secondary-value">
                            {formatCurrency(quoteAmount)}
                            {matchScore && ` (${Math.round(matchScore * 100)}%)`}
                        </span>
                    </div>
                );
            }
        },
        {
            key: 'linked_rfq',
            title: 'Linked Quote',
            type: 'text',
            sortable: true,
            filterable: true,
            render: (_, row: Invoice) => (
                <div className="cell-stacked">
                    <span className="primary-value">{row.comparison?.linked_quote_id ? 'Linked' : 'Unlinked'}</span>
                </div>
            )
        },
        {
            key: 'vendor_name',
            title: 'Vendor Name',
            type: 'text',
            editable: true,
            sortable: true,
            width: '160px',
            defaultHidden: true,
            render: (value: string) => value || '-'
        },
        {
            key: 'invoice_number',
            title: 'Invoice #',
            type: 'text',
            editable: true,
            sortable: true,
            width: '120px',
            defaultHidden: true,
            render: (value: string) => value || '-'
        },
        {
            key: 'invoice_date',
            title: 'Invoice Date',
            type: 'text',
            editable: true,
            sortable: true,
            width: '110px',
            defaultHidden: true,
            render: (value: string) => {
                if (!value) return '-';
                try { return new Date(value).toLocaleDateString('tr-TR'); }
                catch { return value; }
            }
        },
        {
            key: 'total_amount',
            title: 'Total Amount',
            type: 'number',
            editable: true,
            sortable: true,
            width: '120px',
            defaultHidden: true,
            render: (value: number, row: any) => {
                const currency = row.extracted_data?.financials?.currency || row.extracted_data?.currency || 'TRY';
                return formatCurrency(value, currency);
            }
        },
        {
            key: 'supplier_tax_id',
            title: 'Tax ID',
            type: 'text',
            editable: true,
            sortable: true,
            width: '120px',
            defaultHidden: true,
            render: (value: string) => value || '-'
        },
        {
            key: 'actions',
            title: 'Actions',
            type: 'action',
            width: '180px',
            sortable: false,
            filterable: false,
            render: (_, row: Invoice) => (
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="action-link-btn" onClick={() => navigate(`/reconciliation/${row.id}`)} title="View Comparison">
                        <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        View
                    </button>
                    {row.comparison?.linked_quote_id ? (
                        <button className="action-link-btn" onClick={async () => {
                            if (window.confirm('Are you sure you want to unlink this invoice?')) {
                                try {
                                    await import('../../services/documentApi').then(m => m.default.unlinkDocument(row.id));
                                    fetchData(pagination);
                                } catch (e) {
                                    alert('Failed to unlink');
                                }
                            }
                        }} title="Unlink Quote">
                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                            Unlink
                        </button>
                    ) : (
                        <button className="action-link-btn" onClick={() => setLinkInvoiceId(row.id)} title="Link Quote manually">
                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                            Link
                        </button>
                    )}
                </div>
            )
        }
    ];

    return (
        <div className="invoice-reconciliation">
            <DataDashboard
                title="Invoice Reconciliation"
                collection="invoices"
                data={flattenedData}
                columns={columns}
                isLoading={isLoading}
                error={error}
                paginationInfo={pagination}
                showAddButton={true}
                addButtonLabel="Upload Invoice"
                onAdd={() => navigate('/bulk-upload')}
                onDataFetch={handleDataFetch}
                showEditButton={true}
                isEditing={isEditing}
                onEditToggle={() => setIsEditing(prev => !prev)}
                onSaveChanges={handleSaveChanges}
                showDeleteButton={false}
                searchBarExtra={filterParam && FILTER_LABELS[filterParam] ? (
                    <span className="filter-badge">
                        {FILTER_LABELS[filterParam]}
                        <button className="filter-badge__close" onClick={clearFilter} title="Clear filter">&times;</button>
                    </span>
                ) : undefined}
                customHeaderActions={
                    <>
                        <button
                            className="action-button"
                            onClick={() => setIsBulkMatchOpen(true)}
                        >
                            Match & Analyze
                        </button>
                        <button className="action-button" onClick={() => setIsSettingsOpen(true)}>
                            Settings
                        </button>
                    </>
                }
            />

            {/* Details Modal */}
            {selectedInvoice && (
                <DiscrepancyModal
                    match={getMatchResult(selectedInvoice)}
                    onClose={() => setSelectedInvoice(null)}
                />
            )}

            {/* Settings Modal */}
            <InvoiceSettingsModal
                isOpen={isSettingsOpen}
                onClose={() => setIsSettingsOpen(false)}
            />

            {/* Bulk Match Modal */}
            <BulkMatchModal
                isOpen={isBulkMatchOpen}
                onClose={() => setIsBulkMatchOpen(false)}
                onMatchStart={() => { }}
                onMatchComplete={(results) => {
                    setIsBulkMatchOpen(false);
                    fetchData(pagination); // Refresh table
                    // Ideally show a success result, but refresh is good for now
                    if (results.length > 0) alert(`Analyzed ${results.length} pairs!`);
                }}
            />

            {/* Manual Link Modal */}
            {linkInvoiceId && (
                <ManualLinkModal
                    isOpen={!!linkInvoiceId}
                    invoiceId={linkInvoiceId}
                    onClose={() => setLinkInvoiceId(null)}
                    onLinkSuccess={handleLinkSuccess}
                />
            )}
        </div>
    );
};

export default InvoiceReconciliation;
